/**
 * Rate limiting utilities to prevent abuse
 */

// Import security configuration
import { SECURITY_CONFIG } from '../config/security';

// In-memory storage for rate limiting (in production, use Redis or similar)
const rateLimitStore = new Map();

/**
 * Rate limiter class
 */
export class RateLimiter {
  constructor(maxRequests = 10, windowMs = 60000) { // 10 requests per minute by default
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  /**
   * Check if request is allowed for given key
   * @param {string} key - Unique identifier (IP, user ID, etc.)
   * @returns {boolean} - Whether request is allowed
   */
  isAllowed(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;
    
    // Get or create rate limit entry
    if (!rateLimitStore.has(key)) {
      rateLimitStore.set(key, []);
    }
    
    const requests = rateLimitStore.get(key);
    
    // Remove old requests outside the window
    const validRequests = requests.filter(timestamp => timestamp > windowStart);
    
    // Check if under limit
    if (validRequests.length >= this.maxRequests) {
      return false;
    }
    
    // Add current request
    validRequests.push(now);
    rateLimitStore.set(key, validRequests);
    
    return true;
  }

  /**
   * Get remaining requests for key
   * @param {string} key - Unique identifier
   * @returns {number} - Remaining requests in current window
   */
  getRemaining(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;
    
    if (!rateLimitStore.has(key)) {
      return this.maxRequests;
    }
    
    const requests = rateLimitStore.get(key);
    const validRequests = requests.filter(timestamp => timestamp > windowStart);
    
    return Math.max(0, this.maxRequests - validRequests.length);
  }

  /**
   * Get reset time for key
   * @param {string} key - Unique identifier
   * @returns {number} - Timestamp when limit resets
   */
  getResetTime(key) {
    const now = Date.now();
    const windowStart = now - this.windowMs;
    
    if (!rateLimitStore.has(key)) {
      return now + this.windowMs;
    }
    
    const requests = rateLimitStore.get(key);
    const validRequests = requests.filter(timestamp => timestamp > windowStart);
    
    if (validRequests.length === 0) {
      return now + this.windowMs;
    }
    
    // Return time when oldest request in window will expire
    const oldestRequest = Math.min(...validRequests);
    return oldestRequest + this.windowMs;
  }

  /**
   * Clear all rate limit data (useful for testing)
   */
  clear() {
    rateLimitStore.clear();
  }
}

// Pre-configured rate limiters for different operations
export const loginRateLimiter = new RateLimiter(
  SECURITY_CONFIG.RATE_LIMITS.LOGIN.MAX_REQUESTS,
  SECURITY_CONFIG.RATE_LIMITS.LOGIN.WINDOW_MS
);
export const apiRateLimiter = new RateLimiter(
  SECURITY_CONFIG.RATE_LIMITS.API.MAX_REQUESTS,
  SECURITY_CONFIG.RATE_LIMITS.API.WINDOW_MS
);
export const qrScanRateLimiter = new RateLimiter(
  SECURITY_CONFIG.RATE_LIMITS.QR_SCAN.MAX_REQUESTS,
  SECURITY_CONFIG.RATE_LIMITS.QR_SCAN.WINDOW_MS
);
export const checkoutRateLimiter = new RateLimiter(
  SECURITY_CONFIG.RATE_LIMITS.CHECKOUT.MAX_REQUESTS,
  SECURITY_CONFIG.RATE_LIMITS.CHECKOUT.WINDOW_MS
);

/**
 * Get client IP (approximation for browser environments)
 * In production, this should be handled by your backend/CDN
 */
export function getClientId() {
  // For browser environments, we'll use a combination of factors
  // In production, you'd get the real IP from your backend
  const userAgent = navigator.userAgent;
  const language = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Create a simple hash-like identifier
  return btoa(`${userAgent}-${language}-${timezone}`).substring(0, 16);
}

/**
 * Hook for React components to use rate limiting
 */
export function useRateLimit(limiter, key = null) {
  const clientId = key || getClientId();
  
  const isAllowed = () => {
    return limiter.isAllowed(clientId);
  };
  
  const getRemaining = () => {
    return limiter.getRemaining(clientId);
  };
  
  const getResetTime = () => {
    return limiter.getResetTime(clientId);
  };
  
  return {
    isAllowed,
    getRemaining,
    getResetTime,
    clientId
  };
}

/**
 * Middleware function for API calls
 */
export function withRateLimit(limiter, key = null) {
  return function(apiFunction) {
    return async function(...args) {
      const clientId = key || getClientId();
      
      if (!limiter.isAllowed(clientId)) {
        const resetTime = limiter.getResetTime(clientId);
        const resetDate = new Date(resetTime);
        
        throw new Error(`Rate limit exceeded. Please try again after ${resetDate.toLocaleTimeString()}`);
      }
      
      return await apiFunction(...args);
    };
  };
}
